import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import styles from './AddPickUpPointModal.module.css';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { fetchWithFormData } from '../util/fetchWithFormData';
import { PickUpPoint } from '../types';
import { SHELF_COMPARTMENT_SIZES } from '../util/constants';
import { toast } from 'react-toastify';

interface AddPickUpPointModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedPickUpPoint?: PickUpPoint | null;
}

type NumberEntry = { id: string; value: string };

// `id` is the server-side compartment id and is null for one the admin has just added;
// `key` is only for React, since a new row has no id to key off yet.
type CompartmentEntry = {
  key: string;
  id: number | null;
  code: string;
  size: string;
  orderId: number | null;
};

const newEntry = (value = ''): NumberEntry => ({ id: crypto.randomUUID(), value });

const newCompartment = (): CompartmentEntry => ({
  key: crypto.randomUUID(),
  id: null,
  code: '',
  size: SHELF_COMPARTMENT_SIZES[0].value,
  orderId: null,
});

const AddPickUpPointModal: React.FC<AddPickUpPointModalProps> = ({ isOpen, onClose, onSuccess, selectedPickUpPoint }) => {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [collectionFee, setCollectionFee] = useState('');
  const [city, setCity] = useState('');
  const [openingTime, setOpeningTime] = useState('');
  const [closingTime, setClosingTime] = useState('');
  const [collectionDisclaimer, setCollectionDisclaimer] = useState('');
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [descriptor, setDescriptor] = useState('');
  const [slackChannelId, setSlackChannelId] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState<string | null>(null);
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [verifiedNumbers, setVerifiedNumbers] = useState<NumberEntry[]>([newEntry()]);
  const [compartments, setCompartments] = useState<CompartmentEntry[]>([]);
  const [errors, setErrors] = useState<{ name?: string; code?: string; collectionFee?: string; compartments?: string }>({});
  const prevRef = useRef<PickUpPoint | null | undefined>(null);

  useEffect(() => {
    if (!photoFile) {
      setPhotoPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(photoFile);
    setPhotoPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  useEffect(() => {
    if (selectedPickUpPoint) {
      setName(selectedPickUpPoint.name);
      setCode(selectedPickUpPoint.code);
      setCollectionFee(selectedPickUpPoint.collection_fee || '');
      setCity(selectedPickUpPoint.city || '');
      setOpeningTime(selectedPickUpPoint.opening_time || '');
      setClosingTime(selectedPickUpPoint.closing_time || '');
      setCollectionDisclaimer(selectedPickUpPoint.collection_disclaimer || '');
      setGoogleMapsUrl(selectedPickUpPoint.google_maps_url || '');
      setDescriptor(selectedPickUpPoint.descriptor || '');
      setSlackChannelId(selectedPickUpPoint.slack_channel_id || '');
      setPhotoFile(null);
      setExistingPhotoUrl(selectedPickUpPoint.photo_url || null);
      setIsDefault(selectedPickUpPoint.default);
      setIsActive(selectedPickUpPoint.active);
      const existing = selectedPickUpPoint.verified_numbers.map((vn) => newEntry(vn.phone_number));
      setVerifiedNumbers(existing.length > 0 ? existing : [newEntry()]);

      setCompartments(
        (selectedPickUpPoint.shelf_compartments || []).map((c) => ({
          key: String(c.id),
          id: c.id,
          code: c.code,
          size: c.size,
          orderId: c.order_id,
        }))
      );
      setErrors({});
    } else if (!isOpen) {
      setName('');
      setCode('');
      setCollectionFee('');
      setCity('');
      setOpeningTime('');
      setClosingTime('');
      setCollectionDisclaimer('');
      setGoogleMapsUrl('');
      setDescriptor('');
      setSlackChannelId('');
      setPhotoFile(null);
      setExistingPhotoUrl(null);
      setIsDefault(false);
      setIsActive(true);
      setVerifiedNumbers([newEntry()]);
      setCompartments([]);
      setErrors({});
    }
    prevRef.current = selectedPickUpPoint;
  }, [isOpen, selectedPickUpPoint]);

  const handleNumberChange = (id: string, value: string) => {
    setVerifiedNumbers((prev) => prev.map((n) => (n.id === id ? { ...n, value } : n)));
  };

  const addNumber = () => {
    setVerifiedNumbers((prev) => [...prev, newEntry()]);
  };

  const removeNumber = (id: string) => {
    setVerifiedNumbers((prev) => prev.filter((n) => n.id !== id));
  };

  const handleCompartmentChange = (key: string, field: 'code' | 'size', value: string) => {
    setCompartments((prev) => prev.map((c) => (c.key === key ? { ...c, [field]: value } : c)));
  };

  const addCompartment = () => {
    setCompartments((prev) => [...prev, newCompartment()]);
  };

  // A compartment holding an order can still be relabelled, but removing it would strand
  // that order, so the backend rejects it and we don't offer the button.
  const removeCompartment = (key: string) => {
    setCompartments((prev) => prev.filter((c) => c.key !== key || c.orderId != null));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const newErrors: { name?: string; code?: string; collectionFee?: string; compartments?: string } = {};
    if (!name.trim()) newErrors.name = 'Name is required.';
    if (!code.trim()) newErrors.code = 'Code is required.';
    if (collectionFee && isNaN(Number(collectionFee))) newErrors.collectionFee = 'Must be a valid number.';

    const compartmentCodes = compartments.map((c) => c.code.trim().toUpperCase());
    if (compartmentCodes.some((c) => !c)) {
      newErrors.compartments = 'Every compartment needs a code.';
    } else {
      const duplicate = compartmentCodes.find((c, i) => compartmentCodes.indexOf(c) !== i);
      if (duplicate) newErrors.compartments = `Compartment ${duplicate} is listed more than once.`;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    const filteredNumbers = verifiedNumbers.map((n) => n.value.trim()).filter((v) => v.length > 0);

    const payload: Record<string, unknown> = {
      pick_up_point: {
        name,
        code,
        collection_fee: collectionFee || null,
        city: city || null,
        opening_time: openingTime || null,
        closing_time: closingTime || null,
        collection_disclaimer: collectionDisclaimer || null,
        google_maps_url: googleMapsUrl.trim() || null,
        descriptor: descriptor.trim() || null,
        slack_channel_id: slackChannelId.trim() || null,
        default: isDefault,
        active: isActive,
      },
      verified_numbers: filteredNumbers,
      shelf_compartments: compartments.map((c) => ({
        ...(c.id == null ? {} : { id: c.id }),
        code: c.code.trim().toUpperCase(),
        size: c.size,
      })),
    };

    try {
      const response = selectedPickUpPoint
        ? await fetchWithAuth(`/pick_up_points/${selectedPickUpPoint.id}`, {
            method: 'PATCH',
            body: JSON.stringify(payload),
          })
        : await fetchWithAuth('/pick_up_points', {
            method: 'POST',
            body: JSON.stringify(payload),
          });

      if (response.ok) {
        if (photoFile) {
          const saved = await response.json().catch(() => null);
          const id = selectedPickUpPoint?.id ?? saved?.id;
          if (id) {
            const photoForm = new FormData();
            photoForm.append('pick_up_point[photo]', photoFile);
            const photoResponse = await fetchWithFormData(`/pick_up_points/${id}`, photoForm, 'PATCH', {
              notifyOnError: false,
            });
            if (!photoResponse.ok) {
              const photoData = await photoResponse.json().catch(() => ({}));
              const reason = photoData.errors?.[0];
              toast.error(
                reason
                  ? `Saved, but the photo could not be uploaded: ${reason}`
                  : 'Saved, but the photo could not be uploaded.'
              );
            }
          }
        }

        onSuccess();
        onClose();
        toast.success(`${name} ${selectedPickUpPoint ? 'updated' : 'added'} successfully!`);
      } else {
        const data = await response.json().catch(() => ({}));
        if (data.errors?.length) {
          toast.error(data.errors[0]);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className={styles.modalContent}>
      <h2 className="text-2xl font-bold mb-4">{selectedPickUpPoint ? 'Edit Pick-Up Point' : 'Add Pick-Up Point'}</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="pickUpPointName" className="block text-gray-700">Name</label>
          <input
            id="pickUpPointName"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="pickUpPointCode" className="block text-gray-700">Code</label>
          <input
            id="pickUpPointCode"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          />
          {errors.code && <p className="text-red-500 text-sm mt-1">{errors.code}</p>}
        </div>

        <div>
          <label htmlFor="pickUpPointFee" className="block text-gray-700">Collection Fee</label>
          <input
            id="pickUpPointFee"
            type="text"
            value={collectionFee}
            onChange={(e) => setCollectionFee(e.target.value)}
            className="w-full px-3 py-2 border rounded"
            placeholder="e.g. 50.00"
          />
          {errors.collectionFee && <p className="text-red-500 text-sm mt-1">{errors.collectionFee}</p>}
        </div>

        <div>
          <label htmlFor="pickUpPointCity" className="block text-gray-700">City</label>
          <input
            id="pickUpPointCity"
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          />
        </div>

        <div className="flex gap-4">
          <div className="flex-1">
            <label htmlFor="pickUpPointOpeningTime" className="block text-gray-700">Opening Time</label>
            <input
              id="pickUpPointOpeningTime"
              type="text"
              value={openingTime}
              onChange={(e) => setOpeningTime(e.target.value)}
              className="w-full px-3 py-2 border rounded"
              placeholder="e.g. 08:00"
            />
          </div>
          <div className="flex-1">
            <label htmlFor="pickUpPointClosingTime" className="block text-gray-700">Closing Time</label>
            <input
              id="pickUpPointClosingTime"
              type="text"
              value={closingTime}
              onChange={(e) => setClosingTime(e.target.value)}
              className="w-full px-3 py-2 border rounded"
              placeholder="e.g. 17:00"
            />
          </div>
        </div>

        <div>
          <label htmlFor="pickUpPointDisclaimer" className="block text-gray-700">Collection Disclaimer</label>
          <textarea
            id="pickUpPointDisclaimer"
            value={collectionDisclaimer}
            onChange={(e) => setCollectionDisclaimer(e.target.value)}
            className="w-full px-3 py-2 border rounded"
            rows={3}
            placeholder="e.g. Please bring your ID for collection"
          />
        </div>

        <div>
          <label htmlFor="pickUpPointDescriptor" className="block text-gray-700">Descriptor</label>
          <input
            id="pickUpPointDescriptor"
            type="text"
            maxLength={60}
            value={descriptor}
            onChange={(e) => setDescriptor(e.target.value)}
            className="w-full px-3 py-2 border rounded"
            placeholder="e.g. Filling station pickup point"
          />
          <p className="text-gray-500 text-sm mt-1">One short line shown under the name when a customer picks this point.</p>
        </div>

        <div>
          <label htmlFor="pickUpPointSlackChannel" className="block text-gray-700">Slack Channel ID</label>
          <input
            id="pickUpPointSlackChannel"
            type="text"
            value={slackChannelId}
            onChange={(e) => setSlackChannelId(e.target.value)}
            className="w-full px-3 py-2 border rounded"
            placeholder="e.g. C0123456789"
          />
          <p className="text-gray-500 text-sm mt-1">New orders at this point are posted here. Add Tini-Bot to the channel first.</p>
        </div>

        <div>
          <label htmlFor="pickUpPointPhoto" className="block text-gray-700">Photo</label>
          <input
            id="pickUpPointPhoto"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
            className="w-full px-3 py-2 border rounded"
          />
          <p className="text-gray-500 text-sm mt-1">Helps customers recognise the place. JPEG, PNG or WebP, up to 5MB.</p>
          {(photoPreviewUrl || existingPhotoUrl) && (
            <img
              src={photoPreviewUrl ?? existingPhotoUrl!}
              alt={photoPreviewUrl ? 'Selected pick-up point preview' : `${name} collection point`}
              className="mt-2 aspect-[1.91] w-full object-cover rounded border"
            />
          )}
        </div>

        <div>
          <label htmlFor="pickUpPointMapsUrl" className="block text-gray-700">Google Maps Link</label>
          <input
            id="pickUpPointMapsUrl"
            type="url"
            value={googleMapsUrl}
            onChange={(e) => setGoogleMapsUrl(e.target.value)}
            className="w-full px-3 py-2 border rounded"
            placeholder="e.g. https://maps.app.goo.gl/..."
          />
          <p className="text-gray-500 text-sm mt-1">Shown to customers when they choose this pick-up point.</p>
        </div>

        <div>
          <div className="flex items-center gap-2">
            <input
              id="pickUpPointDefault"
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="h-4 w-4"
            />
            <label htmlFor="pickUpPointDefault" className="text-gray-700">Default pick-up point</label>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2">
            <input
              id="pickUpPointActive"
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4"
            />
            <label htmlFor="pickUpPointActive" className="text-gray-700">Active</label>
          </div>
          <p className="text-xs text-gray-400 mt-1">Inactive pick-up points are hidden from the customer app.</p>
        </div>

        <div>
          <label className="block text-gray-700 mb-1">Verified Numbers</label>
          <div className="space-y-2">
            {verifiedNumbers.map((entry) => (
              <div key={entry.id}>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={entry.value}
                    onChange={(e) => handleNumberChange(entry.id, e.target.value)}
                    placeholder="e.g. +26876001234"
                    className="flex-1 px-3 py-2 border rounded"
                  />
                  {verifiedNumbers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeNumber(entry.id)}
                      className="text-red-500 hover:text-red-700 text-sm px-2"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addNumber}
            className="mt-2 text-sm text-blue-600 hover:text-blue-800"
          >
            + Add another number
          </button>
        </div>

        <div>
          <label className="block text-gray-700 mb-1">Shelf Compartments</label>
          {compartments.length === 0 ? (
            <p className="text-sm text-gray-500">
              {selectedPickUpPoint
                ? 'No compartments yet for this pick-up point.'
                : 'Add the shelf compartments this pick-up point will have.'}
            </p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {compartments.map((compartment, index) => (
                <div key={compartment.key} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={compartment.code}
                    onChange={(e) => handleCompartmentChange(compartment.key, 'code', e.target.value)}
                    placeholder="e.g. A1"
                    aria-label={`Compartment ${index + 1} code`}
                    className="flex-1 px-3 py-2 border rounded"
                  />
                  <select
                    value={compartment.size}
                    onChange={(e) => handleCompartmentChange(compartment.key, 'size', e.target.value)}
                    aria-label={`Compartment ${index + 1} size`}
                    className="px-3 py-2 border rounded"
                  >
                    {SHELF_COMPARTMENT_SIZES.map((size) => (
                      <option key={size.value} value={size.value}>
                        {size.label}
                      </option>
                    ))}
                  </select>
                  {/* Fixed width so the code/size fields line up whether the row ends in
                      the in-use label or the shorter delete button. */}
                  <div className="w-16 shrink-0 flex items-center justify-center">
                    {compartment.orderId != null ? (
                      <span className="text-xs text-gray-400 whitespace-nowrap">— in use</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => removeCompartment(compartment.key)}
                        aria-label={`Remove compartment ${compartment.code || 'row'}`}
                        className="text-red-500 hover:text-red-700 text-sm flex items-center justify-center"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {errors.compartments && <p className="text-red-500 text-sm mt-1">{errors.compartments}</p>}
          <button
            type="button"
            onClick={addCompartment}
            className="mt-2 text-sm text-blue-600 hover:text-blue-800"
          >
            + Add another compartment
          </button>
        </div>

        <div className="flex justify-end space-x-2">
          <button type="button" onClick={onClose} className="bg-gray-300 text-gray-700 px-4 py-2 rounded">Cancel</button>
          <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded">
            {selectedPickUpPoint ? 'Update' : 'Add Pick-Up Point'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddPickUpPointModal;
